import { Box, Button, Divider, Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResearchPerfEvalCriteriaItemCard } from '@/module/ResearchPerfEval/ResearchPerfEvalCriteriaItemCard';
import { ResearchPerfEvalCriteriaItemCreate } from '@/module/ResearchPerfEval/ResearchPerfEvalCriteriaItemCreate';
import { ResearchPerfEvalCriteriaItemEdit } from '@/module/ResearchPerfEval/ResearchPerfEvalCriteriaItemEdit';
import { ResearchPerfEvalItemValidationAction } from '@/module/ResearchPerfEval/Validation/ResearchPerfEvalItemValidationAction';
import { ResearchPerfEvalItemValidationHistory } from '@/module/ResearchPerfEval/Validation/ResearchPerfEvalItemValidationHistory';
import {
  EvaluationItem,
  isEvalCriteriaAbstractItem,
  isEvalCriteriaArticleItem,
  isEvalCriteriaAwardItem,
  isEvalCriteriaBookItem,
  isEvalCriteriaBooleanItem,
  isEvalCriteriaChapterItem,
  isEvalCriteriaCommitteeItem,
  isEvalCriteriaCourseItem,
  isEvalCriteriaDictionaryItem,
  isEvalCriteriaDoctoralCommitteeItem,
  isEvalCriteriaEditorialTeamItem,
  isEvalCriteriaFreeItem,
  isEvalCriteriaNumberItem,
  isEvalCriteriaPatentItem,
  isEvalCriteriaPresentationItem,
  isEvalCriteriaProceedingItem,
  isEvalCriteriaProjectItem,
  isEvalCriteriaTranslationItem,
} from '@/module/ResearchPerfEval/model/EvalTemplateTypes';
import { getResearchPerfEvalCriteriaDataOptions } from '@/module/ResearchPerfEval/model/ResearchPerfEvalQuery';

import { Content, NoContentPlaceholder } from '@/core/components';

import { Id, assertUnreachable } from '@/feature/utils';

import { ResearchPerfEvalCriteria, ResearchPerfEvalCriteriaDataItem } from '../model/ResearchPerfEvalTypes';

interface ResearchPerfEvalCriteriaGatheringProps {
  criteria: ResearchPerfEvalCriteria;
  evaluationId: Id;
  authorId: Id;

  isResearchPerfEvalClosed: boolean;
}

function extractSelectId(item: EvaluationItem): number | null {
  if (isEvalCriteriaNumberItem(item)) {
    return null;
  }

  if (isEvalCriteriaBooleanItem(item)) {
    return null;
  }

  if (isEvalCriteriaFreeItem(item)) {
    return null;
  }

  if (isEvalCriteriaProceedingItem(item)) {
    return item.proceeding.id;
  }

  if (isEvalCriteriaArticleItem(item)) {
    return item.article.id;
  }

  if (isEvalCriteriaCourseItem(item)) {
    return item.course.id;
  }

  if (isEvalCriteriaAbstractItem(item)) {
    return item.abstractItem.id;
  }

  if (isEvalCriteriaBookItem(item)) {
    return item.book.id;
  }

  if (isEvalCriteriaChapterItem(item)) {
    return item.chapter.id;
  }

  if (isEvalCriteriaCommitteeItem(item)) {
    return item.committee.id;
  }

  if (isEvalCriteriaPatentItem(item)) {
    return item.patent.id;
  }

  if (isEvalCriteriaProjectItem(item)) {
    return item.project.id;
  }

  if (isEvalCriteriaTranslationItem(item)) {
    return item.translation.id;
  }

  if (isEvalCriteriaAwardItem(item)) {
    return item.award.id;
  }

  if (isEvalCriteriaPresentationItem(item)) {
    return item.presentation.id;
  }

  if (isEvalCriteriaEditorialTeamItem(item)) {
    return item.editorialTeam.id;
  }

  if (isEvalCriteriaDoctoralCommitteeItem(item)) {
    return item.doctoralCommittee.id;
  }

  if (isEvalCriteriaDictionaryItem(item)) {
    return item.dictionary.id;
  }

  assertUnreachable(item);
}

export function ResearchPerfEvalCriteriaGathering({
  evaluationId,
  criteria,
  authorId,
  isResearchPerfEvalClosed,
}: ResearchPerfEvalCriteriaGatheringProps) {
  const { data, refetch } = useSuspenseQuery(
    getResearchPerfEvalCriteriaDataOptions(evaluationId, authorId, criteria.orderNo),
  );
  const [isAdding, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  const [editItem, setEditItem] = useState<ResearchPerfEvalCriteriaDataItem | null>(null);
  const closeEdit = () => setEditItem(null);
  const isEditing = editItem != null;

  const { dataItems } = data;
  const alreadySelectedIds = useMemo(
    () =>
      dataItems
        .map(di => di.item)
        .map(extractSelectId)
        .filter(id => id != null),
    [dataItems],
  );

  const { t } = useTranslation();

  if (isEditing) {
    return (
      <ResearchPerfEvalCriteriaItemEdit
        editItem={editItem}
        close={closeEdit}
        evaluationId={evaluationId}
        authorId={authorId}
        orderNo={criteria.orderNo}
        criteriaType={criteria.criteriaType}
        dataEntryHint={criteria.dataEntryHint}
        alreadySelectedIds={alreadySelectedIds}
      />
    );
  }

  if (isAdding) {
    return (
      <ResearchPerfEvalCriteriaItemCreate
        close={closeAdd}
        evaluationId={evaluationId}
        authorId={authorId}
        orderNo={criteria.orderNo}
        criteriaType={criteria.criteriaType}
        dataEntryHint={criteria.dataEntryHint}
        alreadySelectedIds={alreadySelectedIds}
      />
    );
  }

  return (
    <Box
      p="sm"
      component={Stack}
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <Button
          size="xs"
          onClick={openAdd}
        >
          {t('button.add')}
        </Button>
      </Flex>
      <Divider />
      {dataItems.length === 0 && <NoContentPlaceholder title={t('researchPerfEval.noCriteriaItems')} />}
      <Stack gap="xl">
        {dataItems.length > 0 &&
          dataItems.map((dataItem, index) => (
            <Stack
              component={Content}
              gap="3px"
              key={dataItem.id}
            >
              <Flex gap="3px">
                {isResearchPerfEvalClosed && (
                  <ResearchPerfEvalItemValidationAction
                    variant="compact"
                    itemId={dataItem.id}
                    onAfterValidation={refetch}
                  />
                )}
                <ResearchPerfEvalCriteriaItemCard
                  index={index}
                  item={dataItem}
                  evaluationId={evaluationId}
                  authorId={authorId}
                  orderNo={criteria.orderNo}
                  dataEntryHint={criteria.dataEntryHint}
                  onEdit={setEditItem}
                />
              </Flex>
              {isResearchPerfEvalClosed && (
                <ResearchPerfEvalItemValidationHistory
                  defaultIsCollapsed
                  itemId={dataItem.id}
                />
              )}
            </Stack>
          ))}
      </Stack>
    </Box>
  );
}
